import { z } from "zod";
import { isValidCnpj } from "../../../../../utils/format-utils";
import { pipeSeparatedEmails } from "../../../../../utils/zod-schemas";

/**
 * Normaliza a lista de DS para o formato canônico do banco: minúsculas, sem
 * espaço nas pontas de cada item e sem item vazio.
 *
 * Espelha normalizeDs() de gestorImileCadastroFranquia/app.mjs. O split por
 * vírgula vem ANTES de limpar espaço de propósito: remover todo espaço primeiro
 * colaria "tji lms" (vírgula esquecida) numa base só, "tjilms", que viraria uma
 * ops silenciosamente errada.
 */
export const normalizeDsList = (ds: string): string[] =>
  String(ds ?? "")
    .toLowerCase()
    .split(",")
    .map((d) => d.trim())
    .filter(Boolean);

/**
 * Bases da franquia: uma ou mais, separadas por vírgula, só letras e números.
 * A mensagem de erro precisa dizer o formato, porque é o campo que o operador
 * mais erra — e um erro aqui gera uma ops que nenhum arquivo casa.
 */
const dsSchema = z
  .string()
  .min(1, "DS é obrigatória")
  .refine((val) => normalizeDsList(val).length > 0, {
    message: "Informe ao menos uma base, ex.: tji",
  })
  .refine((val) => normalizeDsList(val).every((d) => /^[a-z0-9]+$/.test(d)), {
    message: "Use bases separadas por vírgula, só letras e números. Ex.: tji,lms",
  })
  .refine(
    (val) => {
      const lista = normalizeDsList(val);
      return new Set(lista).size === lista.length;
    },
    { message: "Há base repetida na lista" }
  );

export const imileFranquiaRegistrationSchema = z.object({
  cnpj: z
    .string()
    .min(1, "CNPJ é obrigatório")
    .refine((val) => isValidCnpj(val), {
      message: "CNPJ inválido",
    }),
  razaoSocial: z.string().min(1, "Razão Social é obrigatória"),
  cep: z
    .string()
    .min(1, "CEP é obrigatório")
    .refine((val) => val.replace(/\D/g, "").length === 8, {
      message: "CEP deve ter 8 dígitos",
    }),
  endereco: z.string().min(1, "Endereço é obrigatório"),
  apelido: z
    .string()
    .min(1, "Apelido (prefixo da franquia) é obrigatório")
    .refine((val) => /^[a-z0-9]+$/.test(val.trim().toLowerCase()), {
      message: "O prefixo aceita só letras e números, sem espaço. Ex.: realog",
    }),
  ds: dsSchema,
  emailContato: pipeSeparatedEmails,
  nfsAtivo: z.boolean(),
  nfsSrAtivo: z.boolean(),
  franquiaUuid: z.string().uuid("UUID inválido").optional().or(z.literal("")),
  redutor: z
    .number()
    .min(0, "Redutor deve ser no mínimo 0")
    .max(1, "Redutor deve ser no máximo 1")
    .optional(),
});

export const imileFranquiaEditSchema = z.object({
  apelido: z
    .string()
    .min(1, "Apelido (prefixo da franquia) é obrigatório")
    .refine((val) => /^[a-z0-9]+$/.test(val.trim().toLowerCase()), {
      message: "O prefixo aceita só letras e números, sem espaço. Ex.: realog",
    }),
  ds: dsSchema,
  emailContato: pipeSeparatedEmails,
  nfsAtivo: z.boolean(),
  nfsSrAtivo: z.boolean(),
  franquiaUuid: z.string().uuid("UUID inválido").optional().or(z.literal("")),
  redutor: z
    .number()
    .min(0, "Redutor deve ser no mínimo 0")
    .max(1, "Redutor deve ser no máximo 1")
    .optional(),
});

export type ImileFranquiaRegistrationFormData = z.infer<
  typeof imileFranquiaRegistrationSchema
>;
export type ImileFranquiaEditFormData = z.infer<typeof imileFranquiaEditSchema>;

/**
 * Prévia das ops que o cadastro vai gerar: prefixo + cada base.
 * Mesma regra de IMILE_FRANQUIAS_CTE no backend e de imile_ops_from_ds no
 * parser R. Serve para o operador ver, antes de salvar, o que vai ter de casar
 * com o nome da pasta no S3.
 */
export const previewOpsDerivadas = (apelido: string, ds: string): string[] => {
  const prefixo = String(apelido ?? "").trim().toLowerCase();
  if (!prefixo) return [];
  return normalizeDsList(ds).map((base) => `${prefixo}${base}`);
};
